<template>
    <UiSearchboxMain id="sb_searchbox" :blok="blok" :loading="loading" @submit-storyblok="submitSearch" />
</template>

<script setup lang="ts">
import type { IHeroTitleItem, ISearchboxConfig } from '@nuxt-monorepo/ui/types';

const { blok, titles } = defineProps<{ blok: ISearchboxConfig; titles: IHeroTitleItem[] }>();
const config = useRuntimeConfig();
const loading = ref(false);
const submitSearch = async (body) => {
    loading.value = true;
    await $publicApi('search', {
        method: 'POST',
        body,
        onResponse({ response }) {
            if (response.ok) {
                loading.value = false;
                let redirectUrl = response._data.data.redirect_url;
                // Remove Absolute Link
                redirectUrl = redirectUrl.replace(config?.public.BOOKING_URL, '');

                navigateTo(redirectUrl, { external: true });
            }
        },
        onResponseError({ response }) {
            loading.value = false;
            console.error(response._data.message);
            console.error(response._data.errors);
        },
    });
};
</script>

<style lang="postcss" scoped>
#sb_searchbox {
    @apply bg-transparent;

    :deep(.tabs) {
        @apply hidden;
    }

    :deep(.form) {
        @apply md:h-[470px] xl:h-auto;
        .input-group-container {
            @apply max-md:bg-transparent max-md:relative;
            .group-inputs {
                @apply flex items-center justify-center;
            }
            .input-group {
                @apply relative;

                .time-label {
                    @apply max-md:absolute max-md:-top-[1.125rem];
                }

                .input-icon-append:has(+ select) {
                    @apply hidden;
                }

                &:has(.time-select-input) {
                    @apply max-md:relative max-md:top-0 max-md:right-0 max-md:w-[50%];
                    .time-label {
                        @apply max-md:!left-0;
                    }
                }

                input {
                    @apply relative h-[54px] w-full bg-white text-[0.95rem] text-[#5a5e6b] pr-8 px-[1.3rem] py-4 rounded-[0.1875rem] border-r-[#c7d4dd] border-r border-solid border-[none];
                    @apply border-[#e7e7e7];
                }

                select {
                    @apply h-[54px] text-[#5a5e6b] max-md:pt-0 pl-4 text-[0.95rem] font-normal;
                    &.text-gray-500 {
                        @apply text-[#9da3ae];
                        &:focus {
                            @apply text-[#5a5e6b];
                        }
                    }
                }

                input,
                select {
                    @apply max-md:pl-12 max-md:rounded;
                    &::placeholder {
                        @apply font-medium;
                    }

                    &:focus,
                    &:hover {
                        @apply outline-none border-none;
                    }

                    &:focus-visible {
                        @apply ring-4 ring-secondary outline-none z-10;
                    }
                }
            }

            input[name='pickup'],
            input[name='dropoff'] {
                @apply text-ellipsis whitespace-nowrap;
                &:focus-visible {
                    outline: none;
                }

                & + i.input-icon-append {
                    @apply block text-black;
                }
            }

            input[name='dropoff'] {
                @apply xl:rounded-tl-[initial] xl:rounded-bl-[initial];
            }

            input[name='pickup'] {
                @apply xl:rounded-tr-[initial] xl:rounded-br-[initial];
            }

            select[name='pass'] {
                @apply h-[54px] text-[0.95rem] text-[#5a5e6b] cursor-pointer border-[none];
                @apply inline-block text-sm font-normal leading-tight text-[#5a5e6b] align-middle bg-white border appearance-none md:pl-3 pr-7 py-1.5 rounded-[0.1875rem] border-solid border-[#b9c6d0];
                background: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E")
                    no-repeat right 0.75rem center / 8px 10px;
                @apply bg-white;
            }

            select[name='departure_date'],
            select[name='arrival_date'] {
                background: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E")
                    no-repeat right 0.75rem center / 8px 10px;
                @apply bg-white md:!border-l;
            }

            .input-group-title {
                @apply max-md:relative max-md:top-0 max-md:left-0;
                @apply text-white text-[0.785rem] tracking-[0.03rem] pb-[0.3rem];
            }
            .input-group {
                &.error {
                    input,
                    select {
                        @apply border-2 border-solid !border-[#ff2d46] text-[#ff2d46];
                        &::placeholder {
                            @apply text-[#ff2d46];
                        }
                    }
                }
                .input-icon-prepend,
                .input-icon-append {
                    @apply top-4;
                }
                .iconify {
                    @apply max-md:z-[1] max-md:text-secondary max-md:text-2xl max-md:top-2 md:hidden;

                    &.input-icon-append {
                        @apply hidden;
                    }
                }
                .dropdown-list {
                    @apply w-full max-md:top-12;

                    .typeahead-list-category {
                        @apply bg-[#447bbe];
                    }

                    .dropdown-list-item {
                        white-space: break-spaces;
                        &:hover {
                            @apply bg-[#59c2ef] text-white;
                        }

                        .iconify {
                            @apply block;
                        }

                        .matched-search {
                            @apply font-bold;
                        }
                    }
                }
                .toggle {
                    @apply md:h-[54px];
                    .toggle-inner {
                        &:after {
                            --tw-content: 'No';
                        }
                        &:before {
                            --tw-content: 'Yes';
                        }
                    }
                }
            }

            &.submit {
                @apply md:w-[275px] max-xl:order-9 max-md:mt-4;
                @apply xl:col-end-[inherit] xl:col-start-[inherit];
                button {
                    @apply bg-secondary border-secondary;

                    &:hover {
                        @apply text-white bg-[#3bada4] cursor-pointer border-[#3bada4];
                    }

                    &:focus-visible {
                        @apply ring-4 ring-secondary-200 outline-none;
                    }
                }
            }

            .toggle-switch {
                @apply w-[40px] h-[40px] top-2 shadow-[0_2px_10px_rgba(0,0,0,0.3)];
                right: calc(100% - 50px);
            }

            .toggle-cb:focus-visible + .toggle-label {
                @apply ring-4 ring-secondary outline-none;
            }

            .toggle-cb:checked {
                & + .toggle-label {
                    .toggle-switch {
                        @apply right-2;
                    }

                    .toggle-inner {
                        @apply bg-[#46a3ca];
                    }
                }
            }

            .toggle-inner {
                @apply bg-[#ebeef3] transition-[background] duration-[0.5s];
                &:before {
                    @apply pr-10 bg-transparent;
                }

                &:after {
                    @apply pl-10 bg-transparent;
                }
            }

            &.toggle-input {
                @apply max-xl:w-[124px];
            }
        }

        .col-span-1:has(.toggle-input) {
            @apply max-md:order-6;
        }

        .col-span-1:has(select[name='pass']) {
            @apply max-md:order-6;
        }

        .col-span-2.order-2:has(input[name='dropoff']) {
            @apply xl:ml-[-1.275rem];
        }

        .custom-label {
            @apply hidden;
        }

        .dates.open {
            .close {
                @apply max-md:text-black;
            }
        }

        .dates-container {
            .vc-container {
                @apply rounded-lg border-gray-300/50 shadow-md;
                .vc-pane-header-wrapper {
                    @apply top-0;
                }
            }
        }
    }
}
</style>
